import { Component, OnInit, Output, Input, EventEmitter, ViewChild, inject } from '@angular/core';
import { VoiceCheckService } from './voice-check.service';
import { Observable } from 'rxjs';
import { CheckService } from '@shared/api/check/check.service';
import { ChallengeCriterionType } from '@shared/api/check/models/challenge-criterion-type.enum';
import { v4 as uuidv4 } from 'uuid';
import { waitForUploadComplete } from '@shared/utils/observables';
import { CheckProgress, createRecording, QueueCompleteEvent, Recording, Upload, VoiceConfig } from './models';
import { HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { MediaProcessingStatus } from '@shared/api/check/models/media-processing-status.enum';
import { LoadingStatus } from '@shared/models/loading-status.enum';
import { MediaUploadResponse } from '@shared/api/check/models/media-upload.response';
import { VoiceCheckPage } from './voice-check-page.enum';
import { VoiceRecordComponent } from './voice-record/voice-record.component';
import { HttpStatus } from '@shared/api/common/http-status.enum';
import { ErrorCode } from '@shared/api/identity/models/error-code.enum';

const PREFIX = '[VoiceCheckComponent]';

@Component({
  selector: 'app-voice',
  templateUrl: './voice.component.html',
  styleUrls: ['./voice.component.scss'],
  standalone: false,
})
export class VoiceComponent implements OnInit {
  private readonly checkService = inject(CheckService);
  private readonly voiceCheckService = inject(VoiceCheckService);

  public readonly Page = VoiceCheckPage;
  @Input() config: VoiceConfig;
  @Input() showBrowserProblemLink: boolean = false;
  /**
   * Whenever we have a
   *
   * If this is a challenge, this emits nothing.
   * If this is registration, it returns a MediaProcessingResponse as well as training/verification status.
   */
  @Output() queueComplete = new EventEmitter<QueueCompleteEvent | undefined>();
  @Output() uploadError = new EventEmitter<any>();

  @ViewChild('record', { static: false }) private readonly recordScreen: VoiceRecordComponent;

  public checks: CheckProgress = {
    training: 0,
    trainingDone: false,
    verification: 0,
    verificationDone: false,
  };

  /** The current page. */
  public page = VoiceCheckPage.Media;
  /** A map of uploads, in progress or complete. */
  public recordings: Recording[] = [];
  /** Whether we're currently in the process of waiting for all uploads to complete. */
  public waitingForUploads = false;
  /** Whether we're currently polling for results. New recordings should not be allowed at this time. */
  public polling = false;
  /** Whether the latest result is busy pending evaluation by the host component. */
  public isPendingEvaluation = false;
  /** Whether we're finished and wrapping up. One the queue  */
  public finished = false;

  public get trainingTarget() {
    return this.config.queue.training ?? 0;
  }

  public get verificationTarget() {
    return this.config.queue.verification ?? 0;
  }

  public get showStatuses() {
    return this.config.type === 'register';
  }

  public get locale() {
    return this.config.locale || undefined;
  }

  ngOnInit() {
    if (!this.config) {
      throw Error('No definition was provided');
    }

    if (!this.config.queue.training && !this.config.queue.verification) {
      throw Error('You requested no training AND no verification. Did you forget to request at least one of these?');
    }

    if (this.trainingTarget === 0) {
      this.checks.trainingDone = true;
    }

    if (this.verificationTarget === 0) {
      this.checks.verificationDone = true;
    }

    this.seedRecordings();

    if (this.voiceCheckService.hasAlreadyConsented()) {
      this.moveToPage(VoiceCheckPage.Record);
    } else {
      this.moveToPage(VoiceCheckPage.Media);
    }
  }

  moveToPage(page: VoiceCheckPage) {
    if (this.page === page) {
      return;
    }
    console.debug(PREFIX, `Moving to page "${page}" from "${this.page}"`);
    window.scrollTo({
      left: 0,
      top: 0,
      behavior: 'instant',
    });
    this.page = page;
  }

  mediaAccessNext(): void {
    this.moveToPage(VoiceCheckPage.Record);
  }

  onObtainMediaFailed(): void {
    this.moveToPage(VoiceCheckPage.MediaError);
  }

  public seedRecordings() {
    const count = (this.config.queue.training ?? 0) + (this.config.queue.verification ?? 0);
    this.recordings = new Array(count).fill(0).map(() => createRecording());
    if (this.recordings.length > 0) {
      this.recordings[0].status = LoadingStatus.Pending;
    }
  }

  public next() {
    const r = this.recordings.find((r) => r.status === LoadingStatus.Idle);
    if (r) {
      r.status = LoadingStatus.Pending;
    } else {
      const another = createRecording();
      another.status = LoadingStatus.Pending;
      this.recordings.push(another);
    }
    this.isPendingEvaluation = false;
    this.recordScreen?.unlock();
  }

  async onFileReady(file: Blob) {
    if (!this.checks.trainingDone) {
      this.incrementTraining();
    } else if (!this.checks.verificationDone) {
      this.incrementVerification();
    }

    const recording = this.recordings.find((r) => r.status === LoadingStatus.Pending);
    if (!recording) {
      console.error('VoiceComponent: no pending recording found');
      return;
    }
    const transfer = this.uploadBiometrics(file);
    let response: MediaUploadResponse;
    try {
      response = await this.watchUpload(transfer, recording);
      await this.uploadDone(response, recording);
    } catch (ex) {
      if (ex instanceof HttpErrorResponse && ex.status === HttpStatus.Conflict && ex.error?.error_code === ErrorCode.DuplicateMedia) {
        recording.status = LoadingStatus.Failed;
      } else {
        this.uploadError.emit(ex);
        return;
      }
    }
    this.next();
  }

  private async uploadDone(uploadResponse: MediaUploadResponse, recording: Recording) {
    this.polling = true;

    const config = this.config;
    const { queue } = config;

    if (config.type === 'challenge') {
      this.queueComplete.emit();
      return;
    }

    const result = await this.pollForCheckResult(uploadResponse);

    if (result.status === MediaProcessingStatus.Rejected) {
      recording.status = LoadingStatus.Failed;
    } else {
      recording.status = LoadingStatus.Success;
    }

    if (result.status === MediaProcessingStatus.Trained) {
      this.checks.trainingDone = true;
    } else if (result.status === MediaProcessingStatus.Verified) {
      this.checks.verificationDone = true;
      this.checks.trainingDone = true;
    }

    if (queue.training && !queue.verification && this.checks.trainingDone) {
      this.queueComplete.emit({ result });
      return;
    }

    if (queue.verification && this.checks.verificationDone) {
      this.queueComplete.emit({ result });
      return;
    }

    this.polling = false;
  }

  private incrementTraining() {
    this.checks.training += 1;
  }

  private incrementVerification() {
    this.checks.verification += 1;
  }

  private async watchUpload(transfer: Observable<HttpEvent<MediaUploadResponse>>, recording: Recording) {
    const upload: Upload = {
      id: uuidv4(),
      status: LoadingStatus.Pending,
      progress: 0.0,
      transfer,
    };
    recording.upload = upload;
    const uploadResponse = await waitForUploadComplete(transfer, (progress) => {
      upload.progress = progress;
    });
    upload.status = LoadingStatus.Success;
    return uploadResponse;
  }

  private async pollForCheckResult(upload: MediaUploadResponse) {
    const checkToken = this.config.checkToken;

    try {
      const result = await this.checkService.pollForCheckResult(upload, checkToken);
      return result;
    } catch (ex) {
      console.error('Retrieving result failed', ex);
      return undefined;
    }
  }

  private uploadBiometrics(file: Blob) {
    if (this.config.type === 'challenge') {
      return this.checkService.uploadChallengeBiometrics(
        file,
        ChallengeCriterionType.VoiceMatch,
        this.config.challengeId,
        this.config.checkToken,
      );
    } else {
      return this.checkService.uploadRegisterBiometrics(file, ChallengeCriterionType.VoiceMatch, this.config.checkToken);
    }
  }

  private markQueueComplete() {
    this.finished = true;
    this.queueComplete.emit();
  }
}
